import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Chart from "chart.js/auto";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import "./PopuliReportPage.css";

const API = {
  dashboard: "https://populi-dashboard-7kpxqstxwq-ey.a.run.app",
  report: "https://europe-west3-evosurvey.cloudfunctions.net/populi-dashboard-report",
};

const ROLE_COLORS = ["#347fb1", "#d99a18", "#27946d", "#805ad5", "#d54949", "#2b6cb0"];

function Icon({ name }) {
  const paths = {
    refresh: <path d="M20 11a8 8 0 1 0-2.34 5.66M20 4v7h-7" />,
    report: <path d="M4 19V9m5 10V5m5 14v-7m5 7V3" />,
    excel: <path d="M14 3h5v18H5V3h5m-2 5 5 8m0-8-5 8" />,
    word: <path d="M4 5h16v14H4V5Zm3 4 2 6 3-6 3 6 2-6" />,
    close: <path d="m6 6 12 12M18 6 6 18" />,
  };
  return <svg className="report-icon" viewBox="0 0 24 24" aria-hidden="true">{paths[name]}</svg>;
}

function appendFormData(values) {
  const formData = new FormData();
  Object.entries(values).forEach(([key, value]) => formData.append(key, value));
  return formData;
}

async function postForm(url, values) {
  const response = await fetch(url, { method: "POST", body: appendFormData(values) });
  if (!response.ok) throw new Error(`İstek başarısız (${response.status})`);
  return response.json();
}

function localDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString("tr-TR");
}

function createRoleChartData(history) {
  const dailyRoleCount = {};
  history.forEach((item) => {
    const date = new Date(item.askTime);
    if (Number.isNaN(date.getTime())) return;
    const dateKey = date.toISOString().slice(0, 10);
    const role = item.userRole || "NoRole";
    dailyRoleCount[dateKey] ||= {};
    dailyRoleCount[dateKey][role] = (dailyRoleCount[dateKey][role] || 0) + 1;
  });

  const labels = Object.keys(dailyRoleCount).sort();
  const roles = [...new Set(Object.values(dailyRoleCount).flatMap((item) => Object.keys(item)))];
  return {
    labels,
    datasets: roles.map((role, index) => ({
      label: role,
      data: labels.map((date) => dailyRoleCount[date][role] || 0),
      borderColor: ROLE_COLORS[index % ROLE_COLORS.length],
      backgroundColor: `${ROLE_COLORS[index % ROLE_COLORS.length]}22`,
      fill: false,
      tension: 0.25,
    })),
  };
}

function ReportModal({ report, categoryCanvasRef, roleCanvasRef, onExport, onClose }) {
  return (
    <div className="report-modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="report-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Rapor grafikleri"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="report-modal__header">
          <div>
            <h2>Rapor Grafikleri</h2>
            <p>
              {report.startDate || "Başlangıç"} – {report.finishDate || "Bitiş"}
              <strong> · Toplam {report.history.length} soru</strong>
            </p>
          </div>
          <button className="report-icon-button" type="button" onClick={onClose} aria-label="Kapat">
            <Icon name="close" />
          </button>
        </header>

        <div className="report-chart-card">
          <h3>En Çok Sorulan Konular</h3>
          <div className="report-chart report-chart--tall"><canvas ref={categoryCanvasRef} /></div>
        </div>
        <div className="report-chart-card">
          <h3>Günlük Soru Dağılımı</h3>
          <div className="report-chart"><canvas ref={roleCanvasRef} /></div>
        </div>

        <footer className="report-modal__actions">
          <button className="report-button report-button--ghost" type="button" onClick={onClose}>Kapat</button>
          <button className="report-button report-button--success" type="button" onClick={onExport}>
            <Icon name="word" /> Raporu Aktar
          </button>
        </footer>
      </section>
    </div>
  );
}

export default function PopuliReportPage({ userEmail: emailProp, onUnauthorized }) {
  const [dashboard, setDashboard] = useState({
    totalUserCount: 0,
    userPercentage: 0,
    documentUsage: [],
    dailyTalks: [],
    populiHistory: [],
  });
  const [startDate, setStartDate] = useState("");
  const [finishDate, setFinishDate] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const documentsCanvasRef = useRef(null);
  const conversationsCanvasRef = useRef(null);
  const categoryCanvasRef = useRef(null);
  const roleCanvasRef = useRef(null);
  const documentsChartRef = useRef(null);
  const conversationsChartRef = useRef(null);
  const categoryChartRef = useRef(null);
  const roleChartRef = useRef(null);

  const userEmail =
    emailProp || (typeof window !== "undefined" ? sessionStorage.getItem("userEmail") : "") || "";

  const loadDashboard = useCallback(async () => {
    if (!userEmail) return;
    setLoading(true);
    setError("");
    try {
      const data = await postForm(API.dashboard, { useremail: userEmail });
      setDashboard({
        totalUserCount: Number(data.totalUserCount) || 0,
        userPercentage: Number(data.userPercentage) || 0,
        documentUsage: Array.isArray(data.documentUsage) ? data.documentUsage : [],
        dailyTalks: Array.isArray(data.dailyTalks) ? data.dailyTalks : [],
        populiHistory: Array.isArray(data.populiHistory) ? data.populiHistory : [],
      });
    } catch (requestError) {
      setError(requestError.message || "Dashboard verileri yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }, [userEmail]);

  useEffect(() => {
    if (!userEmail) {
      onUnauthorized?.();
      return;
    }
    loadDashboard();
  }, [loadDashboard, onUnauthorized, userEmail]);

  useEffect(() => {
    if (!documentsCanvasRef.current || !conversationsCanvasRef.current) return undefined;
    documentsChartRef.current?.destroy();
    conversationsChartRef.current?.destroy();

    documentsChartRef.current = new Chart(documentsCanvasRef.current, {
      type: "bar",
      data: {
        labels: dashboard.documentUsage.map((item) => item.document),
        datasets: [{
          label: "Erişim Sayısı",
          data: dashboard.documentUsage.map((item) => item.usage),
          backgroundColor: "rgba(52, 127, 177, .65)",
          borderColor: "#347fb1",
          borderWidth: 1,
          borderRadius: 5,
        }],
      },
      options: { responsive: true, maintainAspectRatio: false, scales: { y: { beginAtZero: true } } },
    });

    conversationsChartRef.current = new Chart(conversationsCanvasRef.current, {
      type: "line",
      data: {
        labels: dashboard.dailyTalks.map((item) => item.talkDate),
        datasets: [{
          label: "Soru Sayısı",
          data: dashboard.dailyTalks.map((item) => item.talkCount),
          backgroundColor: "rgba(39, 148, 109, .14)",
          borderColor: "#27946d",
          fill: true,
          tension: 0.35,
        }],
      },
      options: { responsive: true, maintainAspectRatio: false, scales: { y: { beginAtZero: true } } },
    });

    return () => {
      documentsChartRef.current?.destroy();
      conversationsChartRef.current?.destroy();
    };
  }, [dashboard.dailyTalks, dashboard.documentUsage]);

  useEffect(() => {
    if (!report || !categoryCanvasRef.current || !roleCanvasRef.current) return undefined;
    const frequency = {};
    report.history.forEach((item) => {
      (Array.isArray(item.category) ? item.category : []).forEach((category) => {
        frequency[category] = (frequency[category] || 0) + 1;
      });
    });
    const sortedCategories = Object.entries(frequency).sort((a, b) => b[1] - a[1]);

    categoryChartRef.current?.destroy();
    roleChartRef.current?.destroy();
    categoryChartRef.current = new Chart(categoryCanvasRef.current, {
      type: "bar",
      data: {
        labels: sortedCategories.map(([category]) => category),
        datasets: [{
          label: "Kategori Frekansı",
          data: sortedCategories.map(([, count]) => count),
          backgroundColor: "rgba(52, 127, 177, .65)",
          borderColor: "#347fb1",
          borderWidth: 1,
          borderRadius: 4,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: "y",
        scales: { x: { beginAtZero: true } },
      },
    });

    roleChartRef.current = new Chart(roleCanvasRef.current, {
      type: "line",
      data: createRoleChartData(report.history),
      options: { responsive: true, maintainAspectRatio: false, scales: { y: { beginAtZero: true } } },
    });

    return () => {
      categoryChartRef.current?.destroy();
      roleChartRef.current?.destroy();
    };
  }, [report]);

  const talkPercentage = useMemo(() => {
    if (!dashboard.totalUserCount) return "0.00";
    return ((dashboard.userPercentage / dashboard.totalUserCount) * 100).toFixed(2);
  }, [dashboard.totalUserCount, dashboard.userPercentage]);

  async function loadReport() {
    if (startDate && finishDate && startDate > finishDate) {
      setError("Başlangıç tarihi bitiş tarihinden sonra olamaz.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const data = await postForm(API.report, {
        useremail: userEmail,
        start: startDate,
        finish: finishDate,
      });
      setReport({
        history: Array.isArray(data.populiHistory) ? data.populiHistory : [],
        startDate,
        finishDate,
      });
    } catch (requestError) {
      setError(requestError.message || "Rapor oluşturulamadı.");
    } finally {
      setLoading(false);
    }
  }

  function exportExcel() {
    const rows = dashboard.populiHistory.map((row) => ({
      "Kullanıcı Sorusu": row.userQuestion,
      "Populi Cevap": row.aiAnswer,
      "Kaynak Doküman": row.document,
      "Kullanıcı Rolü": row.userRole,
      "Soru Zamanı": localDate(row.askTime),
    }));
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Populi History");
    XLSX.writeFile(workbook, "PopuliHistory.xlsx");
  }

  async function exportWord() {
    if (!report || !categoryCanvasRef.current || !roleCanvasRef.current) return;
    setLoading(true);
    try {
      const categoryChart = categoryCanvasRef.current.toDataURL("image/png");
      const roleChart = roleCanvasRef.current.toDataURL("image/png");
      const reportHtml = `<!doctype html>
        <html><head><meta charset="utf-8"><title>Populi Rapor</title></head>
        <body>
          <h1>Populi Rapor</h1>
          <h2>${report.startDate || "Başlangıç"} – ${report.finishDate || "Bitiş"} · Toplam ${report.history.length} soru</h2>
          <h2>En Çok Sorulan Konular</h2><p><img src="${categoryChart}" width="600" alt="Kategori grafiği"></p>
          <h2>Günlük Soru Dağılımı</h2><p><img src="${roleChart}" width="600" alt="Günlük soru grafiği"></p>
        </body></html>`;
      saveAs(new Blob([reportHtml], { type: "application/msword;charset=utf-8" }), "PopuliRapor.doc");
    } catch (exportError) {
      setError(exportError.message || "Word raporu oluşturulamadı.");
    } finally {
      setLoading(false);
    }
  }

  if (!userEmail) {
    return <div className="report-page report-empty">Bu sayfayı görüntülemek için giriş yapmalısınız.</div>;
  }

  return (
    <main className="report-page">
      <section className="report-shell">
        <div className="report-toolbar">
          <button className="report-button report-button--secondary" onClick={loadDashboard} disabled={loading}>
            <Icon name="refresh" /> Yenile
          </button>
          <div className="report-filters">
            <label>Başlangıç Tarihi<input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} /></label>
            <label>Bitiş Tarihi<input type="date" value={finishDate} onChange={(event) => setFinishDate(event.target.value)} /></label>
            <button className="report-button report-button--success" onClick={loadReport} disabled={loading}>
              <Icon name="report" /> AI Rapor
            </button>
          </div>
        </div>

        {error && <div className="report-alert" role="alert">{error}</div>}

        <div className="report-stats">
          <article className="report-stat report-stat--blue">
            <h2>Toplam Kullanıcı Sayısı</h2><strong>{dashboard.totalUserCount}</strong>
          </article>
          <article className="report-stat report-stat--slate">
            <h2>Bugünkü Konuşma</h2><strong>{talkPercentage}%</strong>
          </article>
        </div>

        <section className="report-card">
          <h2>Son 7 Günlük En Çok Başvurulan 10 Doküman</h2>
          <div className="report-chart"><canvas ref={documentsCanvasRef} /></div>
        </section>

        <section className="report-card">
          <h2>Son 7 Günlük Konuşma Grafiği</h2>
          <div className="report-chart"><canvas ref={conversationsCanvasRef} /></div>
        </section>

        <section className="report-card">
          <div className="report-card__header">
            <h2>Populi Cevaplar (Son 50 Konuşma)</h2>
            <button className="report-button report-button--excel" onClick={exportExcel} disabled={!dashboard.populiHistory.length}>
              <Icon name="excel" /> Excel’e Aktar
            </button>
          </div>
          <div className="report-table-wrap">
            <table className="report-table">
              <thead><tr><th>Soru</th><th>Populi Cevap</th><th>Kaynak Doküman</th><th>Zaman</th></tr></thead>
              <tbody>
                {!dashboard.populiHistory.length && <tr><td colSpan="4" className="report-table__empty">Kayıt bulunmuyor.</td></tr>}
                {dashboard.populiHistory.slice(0, 50).map((row, index) => (
                  <tr key={`${row.askTime || "row"}-${index}`}>
                    <td>{row.userQuestion || "—"}</td>
                    <td>{row.aiAnswer || "—"}</td>
                    <td>{row.document || "—"}</td>
                    <td>{localDate(row.askTime)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </section>

      {report && (
        <ReportModal
          report={report}
          categoryCanvasRef={categoryCanvasRef}
          roleCanvasRef={roleCanvasRef}
          onExport={exportWord}
          onClose={() => setReport(null)}
        />
      )}

      {loading && <div className="report-loading" role="status"><span /> İşlem yapılıyor…</div>}
    </main>
  );
}
